// src/pages/Admin/SemestersView.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function SemestersView() {
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSemesters();
  }, []);

  const fetchSemesters = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/semesters');
      let data = Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) => b.id - a.id);
      setSemesters(data);
    } catch (err) {
      setError('Error fetching semesters.');
    }
    setLoading(false);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedSemesters = [...semesters].sort((a, b) => {
    const aVal = (a[sortColumn] || '').toString().toLowerCase();
    const bVal = (b[sortColumn] || '').toString().toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredSemesters = sortedSemesters.filter(s =>
    s.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleEdit = (id) => {
    navigate(`/admin/semesters/edit/${id}`);
  };

  const handleDelete = (id, canDelete) => {
    if (canDelete === false) {
      setTooltip('This semester cannot be deleted.');
      return;
    }
    setDeleteId(id);
  };

  const confirmDeletion = async () => {
    try {
      await axios.delete(`/api/semesters/${deleteId}`);
      setTooltip('Semester deleted successfully!');
      fetchSemesters();
      setDeleteId(null);
    } catch (err) {
      setTooltip('Error deleting semester.');
      setDeleteId(null);
    }
  };

  const cancelDeletion = () => {
    setDeleteId(null);
  };

  const columns = [
    { key: 'id', header: <>ID</> },
    { key: 'name', header: <>Semester {sortColumn === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}</> },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (s) => (
    <>
      <td>{s.id}</td>
      <td>{s.name}</td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(s.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id, s.canDelete)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container-fluid mt-2 px-4">
      <TitleWithBack title="View All Semesters" />
      <div className="mb-3">
        <input 
          type="text"
          className="form-control"
          placeholder="Search semesters..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
      </div>
      {loading ? (
        <p>Loading semesters...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredSemesters.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={filteredSemesters}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {deleteId && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this semester?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
    </div>
  );
}

export default SemestersView;
